if (!customElements.get('tiger-product-anchor-nav')) {
  class TigerProductAnchorNav extends HTMLElement {
    connectedCallback() {
      this.links = Array.from(this.querySelectorAll('[data-anchor-link]'));
      this.sentinel = this.querySelector('.tiger-product-anchor-nav__sentinel');
      this.stickyElement = this.querySelector('.tiger-product-anchor-nav__sticky');
      this.sectionElement = this.closest('.tiger-product-anchor-nav-section');
      this.header = document.querySelector('.section-header');
      this.targets = this.links
        .map((link) => {
          const targetId = link.getAttribute('data-target-id');
          if (!targetId) return null;
          const target = this.resolveTarget(targetId);
          if (!target) return null;
          return { link, target };
        })
        .filter(Boolean);

      this.onClick = this.onClick.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.updateStickyState = this.updateStickyState.bind(this);
      this.onResize = this.onResize.bind(this);

      this.links.forEach((link) => link.addEventListener('click', this.onClick));

      this.updateMetrics();
      this.updateStickyState();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', this.onResize);

      if ('IntersectionObserver' in window && this.targets.length) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          rootMargin: '-25% 0px -55% 0px',
          threshold: [0, 0.2, 0.5, 1],
        });

        this.targets.forEach(({ target }) => this.observer.observe(target));
      }
    }

    disconnectedCallback() {
      this.links?.forEach((link) => link.removeEventListener('click', this.onClick));
      this.observer?.disconnect();
      if (this.rafId) window.cancelAnimationFrame(this.rafId);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onResize);
    }

    onClick(event) {
      const link = event.currentTarget;
      const targetId = link.getAttribute('data-target-id');
      if (!targetId) return;

      const target = this.resolveTarget(targetId);
      if (!target) return;

      event.preventDefault();

      const stickyOffset = (this.classList.contains('is-stuck') ? this.stuckHeight : this.baseHeight) || 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - stickyOffset - 12;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      });
    }

    onResize() {
      this.updateMetrics();
      this.updateStickyState();
    }

    onScroll() {
      if (this.rafId) return;

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.updateStickyState();
      });
    }

    updateStickyState() {
      if (!this.sectionElement || !this.stickyElement) return;

      const headerOffset = this.getHeaderOffset();
      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const shouldStick = scrollTop + headerOffset >= this.sectionStart;

      this.classList.toggle('is-stuck', shouldStick);
    }

    onIntersect(entries) {
      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (!visibleEntries.length) return;

      const activeTarget = visibleEntries[0].target;
      const activeItem = this.targets.find(({ target }) => target === activeTarget);
      if (!activeItem) return;

      this.links.forEach((link) => {
        link.classList.toggle('is-active', link === activeItem.link);
      });
    }

    updateMetrics() {
      if (!this.stickyElement) return;

      this.baseHeight = this.stickyElement.offsetHeight;
      this.stuckHeight = this.baseHeight;
      this.sectionStart = this.getDocumentTop(this.sectionElement);
    }

    getDocumentTop(element) {
      let top = 0;
      let currentElement = element;

      while (currentElement) {
        top += currentElement.offsetTop || 0;
        currentElement = currentElement.offsetParent;
      }

      return top;
    }

    resolveTarget(targetId) {
      if (targetId === 'judge-me-reviews') {
        const widget = document.querySelector(
          '#judgeme_product_reviews, .jdgm-review-widget, .jdgm-widget.jdgm-review-widget'
        );

        return widget?.closest('.shopify-section') || widget;
      }

      return document.getElementById(targetId);
    }

    getHeaderOffset() {
      if (this.header?.classList.contains('shopify-section-header-hidden')) {
        return 0;
      }

      const headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
      const parsedHeight = Number.parseFloat(headerHeight);

      return Number.isNaN(parsedHeight) ? 0 : parsedHeight;
    }
  }

  customElements.define('tiger-product-anchor-nav', TigerProductAnchorNav);
}
